import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  TrendingUp, TrendingDown, Euro, Target, Award, AlertTriangle,
  Plus, Loader2, Upload, X, Trash2, Edit3, BarChart3,
} from "lucide-react";

const PERIOD_OPTIONS = [
  { value: 7, label: "7 giorni" },
  { value: 30, label: "30 giorni" },
  { value: 90, label: "90 giorni" },
];

const SOURCE_LABEL = {
  "maxvalle.it": "maxvalle.it",
  "sitidaincubo.it": "sitidaincubo.it",
  "google_ads": "Google Ads",
  "facebook_ads": "Facebook Ads",
  "linkedin_ads": "LinkedIn Ads",
  "direct": "Diretto",
};

const fmtEur = (n) => n == null ? "—" : `€ ${new Intl.NumberFormat("it-IT", {maximumFractionDigits: 0}).format(n)}`;
const fmtPct = (n) => n == null ? "—" : `${n.toFixed(1)}%`;

function delta(curr, prev) {
  if (prev === 0 || prev == null) return null;
  return ((curr - prev) / prev) * 100;
}

function DeltaBadge({ value, inverse = false }) {
  if (value == null) return null;
  const isPos = value >= 0;
  const isGood = inverse ? !isPos : isPos;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, marginLeft: 6,
      color: isGood ? "#15803d" : "#b91c1c",
    }}>
      {isPos ? "▲" : "▼"} {Math.abs(value).toFixed(0)}%
    </span>
  );
}

export default function RoiDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [spends, setSpends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpendModal, setShowSpendModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/inbound/roi?days=${days}&compare=true`),
        api.get("/ad-spend"),
      ]);
      setData(r1.data);
      setSpends(r2.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days]);

  const sources = data?.current?.sources || [];
  const totals = data?.current?.totals || {};
  const prev = data?.previous_totals || {};

  // Compute best/worst performers (need at least 1 request)
  const ranked = useMemo(() => {
    const elig = sources.filter(s => s.requests > 0);
    // Best by ROI eur (or CPL qualified if no revenue yet)
    const byRoi = [...elig].sort((a, b) => (b.roi_eur || 0) - (a.roi_eur || 0));
    const byHot = [...elig].sort((a, b) => (b.hot_lead_rate || 0) - (a.hot_lead_rate || 0));
    return { best: byRoi[0] || byHot[0], worst: byRoi[byRoi.length - 1] };
  }, [sources]);

  if (loading && !data) {
    return (
      <div className="card text-center" style={{padding: 40}}>
        <Loader2 size={24} className="spin" style={{margin: "0 auto"}}/>
      </div>
    );
  }

  return (
    <div data-testid="roi-dashboard">
      {/* Header + period picker */}
      <div className="flex items-center justify-between mb-4" style={{flexWrap:"wrap",gap:12}}>
        <div className="flex items-center gap-2">
          <BarChart3 size={20} color="var(--mv-primary)"/>
          <div className="h-section" style={{margin: 0}}>Dashboard ROI Sorgenti</div>
        </div>
        <div className="flex gap-2 items-center">
          {PERIOD_OPTIONS.map(p => (
            <button key={p.value} onClick={() => setDays(p.value)}
              data-testid={`period-${p.value}`}
              className={"btn btn-sm " + (days === p.value ? "btn-primary" : "btn-ghost")}
              style={{padding:"4px 12px"}}>
              {p.label}
            </button>
          ))}
          <button onClick={() => setShowSpendModal(true)} className="btn btn-ghost btn-sm"
            data-testid="manage-spend-btn">
            <Plus size={14}/> Spesa manuale
          </button>
          <button onClick={() => setShowCsvModal(true)} className="btn btn-ghost btn-sm"
            data-testid="import-csv-btn">
            <Upload size={14}/> Import CSV
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid gap-3 mb-4" style={{gridTemplateColumns:"repeat(auto-fit, minmax(180px,1fr))"}}>
        <KpiTile label="Spesa totale" value={fmtEur(totals.spend_eur)}
          delta={delta(totals.spend_eur, prev.spend_eur)} inverse data-testid="kpi-spend"/>
        <KpiTile label="Lead ricevuti" value={totals.requests || 0}
          delta={delta(totals.requests, prev.requests)} accent="primary"/>
        <KpiTile label="CPL medio"
          value={totals.cpl_eur != null ? fmtEur(totals.cpl_eur) : "—"}
          delta={delta(totals.cpl_eur, prev.cpl_eur)} inverse
          tip="Cost Per Lead — spesa / numero richieste inbound"/>
        <KpiTile label="CPL qualificato"
          value={totals.cpl_qualified_eur != null ? fmtEur(totals.cpl_qualified_eur) : "—"}
          delta={delta(totals.cpl_qualified_eur, prev.cpl_qualified_eur)} inverse
          tip="Lead 'qualificato' = ha aperto la mail o cliccato il calendario"/>
        <KpiTile label="Clienti convertiti" value={totals.customers || 0} accent="success"
          delta={delta(totals.customers, prev.customers)}/>
        <KpiTile label="CAC (Customer Acq. Cost)"
          value={totals.cac_eur != null ? fmtEur(totals.cac_eur) : "—"}
          tip="Spesa / numero clienti acquisiti nel periodo" inverse/>
        <KpiTile label="Ricavi" value={fmtEur(totals.revenue_eur)}
          delta={delta(totals.revenue_eur, prev.revenue_eur)} accent="success"/>
        <KpiTile label="ROI"
          value={totals.roi_eur != null ? fmtEur(totals.roi_eur) : "—"}
          subValue={totals.roi_multiple != null ? `${totals.roi_multiple.toFixed(2)}×` : ""}
          accent={totals.roi_eur >= 0 ? "success" : "danger"}/>
      </div>

      {/* Best / Worst */}
      {ranked.best && (
        <div className="grid gap-3 mb-4" style={{gridTemplateColumns:"1fr 1fr"}}>
          <PerformerCard kind="best" source={ranked.best}/>
          {ranked.worst && ranked.worst.source !== ranked.best.source && (
            <PerformerCard kind="worst" source={ranked.worst}/>
          )}
        </div>
      )}

      {/* Funnel chart (CSS-only) */}
      {totals.requests > 0 && (
        <div className="card mb-4" style={{padding: 18}}>
          <div className="text-small text-muted mb-3" style={{fontWeight: 600, textTransform: "uppercase"}}>
            Funnel di conversione · ultimi {days} giorni
          </div>
          <Funnel totals={totals}/>
        </div>
      )}

      {/* Sources detail table */}
      <div className="card" style={{padding: 0, overflow: "hidden"}}>
        <div style={{padding: "12px 16px", borderBottom: "1px solid #eee"}}>
          <div style={{fontWeight: 600}}>Dettaglio per sorgente</div>
          <div className="text-small text-muted mt-1">
            Le sorgenti con <strong>0 lead</strong> ma con spese registrate sono mostrate
            per evidenziare il problema (mai convertite).
          </div>
        </div>
        {sources.length === 0 ? (
          <div className="text-center text-muted" style={{padding: 40}}>
            Nessun dato nel periodo selezionato. Configura le sorgenti UTM nei tuoi form
            o inserisci una spesa pubblicitaria per iniziare a tracciare il ROI.
          </div>
        ) : (
          <div style={{overflowX: "auto"}}>
          <table className="w-full" style={{borderCollapse: "collapse", fontSize: 13}}>
            <thead>
              <tr style={{background:"#fafafa", fontSize:11, textTransform:"uppercase", color:"var(--mv-text-muted)"}}>
                <th style={{textAlign:"left", padding:"10px 12px"}}>Sorgente</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>Spesa</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>Lead</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>CPL</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>Aperture</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>Hot lead</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>Clienti</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>CAC</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>Ricavi</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>ROI</th>
              </tr>
            </thead>
            <tbody>
              {sources.map(s => (
                <tr key={s.source} data-testid={`source-${s.source}`}
                  style={{borderTop: "1px solid #f0f0f0"}}>
                  <td style={{padding: "10px 12px", fontWeight: 600}}>
                    {SOURCE_LABEL[s.source] || s.source}
                    {s.reused_cache > 0 && (
                      <div className="text-small text-muted" style={{fontWeight:400}}>
                        ♻ {s.reused_cache} cache hit
                      </div>
                    )}
                  </td>
                  <td style={{padding: "10px 12px", textAlign: "right"}}>{fmtEur(s.spend_eur)}</td>
                  <td style={{padding: "10px 12px", textAlign: "right", fontWeight: 600}}>{s.requests}</td>
                  <td style={{padding: "10px 12px", textAlign: "right"}}>
                    {s.cpl_eur != null ? fmtEur(s.cpl_eur) : "—"}
                  </td>
                  <td style={{padding: "10px 12px", textAlign: "right"}}>
                    {s.email_opened}/{s.email_sent}
                    <div className="text-small text-muted" style={{fontWeight:400}}>{fmtPct(s.open_rate)}</div>
                  </td>
                  <td style={{padding: "10px 12px", textAlign: "right",
                    color: s.hot_lead_rate >= 20 ? "var(--mv-success)" : s.hot_lead_rate >= 10 ? "#d97706" : "var(--mv-text-muted)"}}>
                    {s.calendar_clicks}
                    <div className="text-small" style={{fontWeight:400}}>{fmtPct(s.hot_lead_rate)}</div>
                  </td>
                  <td style={{padding: "10px 12px", textAlign: "right", fontWeight: 600,
                    color: s.customers > 0 ? "var(--mv-success)" : "var(--mv-text-muted)"}}>
                    {s.customers}
                  </td>
                  <td style={{padding: "10px 12px", textAlign: "right"}}>
                    {s.cac_eur != null ? fmtEur(s.cac_eur) : "—"}
                  </td>
                  <td style={{padding: "10px 12px", textAlign: "right"}}>{fmtEur(s.revenue_eur)}</td>
                  <td style={{padding: "10px 12px", textAlign: "right", fontWeight: 600,
                    color: (s.roi_eur || 0) >= 0 ? "var(--mv-success)" : "var(--mv-danger)"}}>
                    {fmtEur(s.roi_eur)}
                    {s.roi_multiple != null && s.roi_multiple > 0 && (
                      <div className="text-small text-muted" style={{fontWeight:400}}>
                        {s.roi_multiple.toFixed(2)}×
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Existing spend records */}
      <div className="card mt-4" style={{padding: 0}}>
        <div style={{padding: "12px 16px", borderBottom: "1px solid #eee", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <div style={{fontWeight: 600}}>Spese pubblicitarie registrate</div>
          <span className="text-small text-muted">{spends.length} record</span>
        </div>
        {spends.length === 0 ? (
          <div className="text-center text-muted" style={{padding: 28}}>
            Nessuna spesa registrata. Clicca "Spesa manuale" per inserirne una, oppure "Import CSV"
            per caricare il report di Google Ads / Facebook Ads.
          </div>
        ) : (
          <div style={{overflowX: "auto"}}>
          <table className="w-full" style={{borderCollapse: "collapse", fontSize: 13}}>
            <thead>
              <tr style={{background:"#fafafa", fontSize:11, textTransform:"uppercase", color:"var(--mv-text-muted)"}}>
                <th style={{textAlign:"left", padding:"10px 12px"}}>Sorgente</th>
                <th style={{textAlign:"left", padding:"10px 12px"}}>Canale</th>
                <th style={{textAlign:"left", padding:"10px 12px"}}>Periodo</th>
                <th style={{textAlign:"right", padding:"10px 12px"}}>Importo</th>
                <th style={{textAlign:"left", padding:"10px 12px"}}>Note</th>
                <th style={{padding:"10px 12px"}}></th>
              </tr>
            </thead>
            <tbody>
              {spends.map(s => (
                <tr key={s.id} style={{borderTop: "1px solid #f0f0f0"}}>
                  <td style={{padding: "8px 12px", fontWeight: 600}}>{SOURCE_LABEL[s.source] || s.source}</td>
                  <td style={{padding: "8px 12px"}}>{s.channel || "—"}</td>
                  <td style={{padding: "8px 12px"}}>{s.periodStart} → {s.periodEnd}</td>
                  <td style={{padding: "8px 12px", textAlign: "right", fontWeight: 600}}>{fmtEur(s.amountEur)}</td>
                  <td style={{padding: "8px 12px"}} className="text-small text-muted">{s.notes || ""}</td>
                  <td style={{padding: "8px 12px", textAlign: "right"}}>
                    <button onClick={async () => {
                      if (!window.confirm(`Eliminare la spesa di ${fmtEur(s.amountEur)} per ${s.source}?`)) return;
                      await api.delete(`/ad-spend/${s.id}`);
                      await load();
                    }} className="btn btn-ghost btn-sm" data-testid={`del-spend-${s.id}`}
                      style={{color:"var(--mv-danger)"}}>
                      <Trash2 size={13}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showSpendModal && <SpendModal onClose={()=>setShowSpendModal(false)} onSaved={() => { setShowSpendModal(false); load(); }}/>}
      {showCsvModal && <CsvImportModal onClose={()=>setShowCsvModal(false)} onSaved={() => { setShowCsvModal(false); load(); }}/>}
    </div>
  );
}


function KpiTile({ label, value, subValue, delta, inverse, accent, tip }) {
  const colorMap = {
    primary: "var(--mv-primary)",
    success: "var(--mv-success)",
    danger: "var(--mv-danger)",
  };
  return (
    <div className="card" style={{padding: 14}} title={tip || ""}>
      <div className="stat-label">{label}</div>
      <div className="stat-num" style={{color: colorMap[accent] || undefined}}>
        {value}
        {subValue && <span style={{fontSize: 14, fontWeight: 500, color: "var(--mv-text-muted)", marginLeft: 6}}>{subValue}</span>}
      </div>
      {delta != null && Number.isFinite(delta) && (
        <div className="text-small mt-1">
          vs periodo precedente
          <DeltaBadge value={delta} inverse={inverse}/>
        </div>
      )}
    </div>
  );
}


function PerformerCard({ kind, source }) {
  const isBest = kind === "best";
  const Icon = isBest ? Award : AlertTriangle;
  const color = isBest ? "var(--mv-success)" : "#b91c1c";
  const bg = isBest ? "#dcfce7" : "#fee2e2";
  return (
    <div className="card" style={{padding: 16, borderLeft: `4px solid ${color}`}}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} color={color}/>
        <span style={{fontWeight: 600, color, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5}}>
          {isBest ? "Migliore performer" : "Da ottimizzare"}
        </span>
      </div>
      <div style={{fontSize: 18, fontWeight: 700, marginBottom: 6}}>
        {SOURCE_LABEL[source.source] || source.source}
      </div>
      <div className="grid gap-2" style={{gridTemplateColumns: "1fr 1fr"}}>
        <div className="text-small">
          <span className="text-muted">Lead:</span> <strong>{source.requests}</strong>
        </div>
        <div className="text-small">
          <span className="text-muted">Spesa:</span> <strong>{fmtEur(source.spend_eur)}</strong>
        </div>
        <div className="text-small">
          <span className="text-muted">CPL:</span> <strong>{source.cpl_eur != null ? fmtEur(source.cpl_eur) : "—"}</strong>
        </div>
        <div className="text-small">
          <span className="text-muted">Hot rate:</span> <strong>{fmtPct(source.hot_lead_rate)}</strong>
        </div>
        <div className="text-small">
          <span className="text-muted">Clienti:</span> <strong>{source.customers}</strong>
        </div>
        <div className="text-small">
          <span className="text-muted">ROI:</span> <strong style={{color: (source.roi_eur||0) >= 0 ? "var(--mv-success)" : "var(--mv-danger)"}}>{fmtEur(source.roi_eur)}</strong>
        </div>
      </div>
    </div>
  );
}


function Funnel({ totals }) {
  // Stages from top to bottom
  const stages = [
    { label: "Lead ricevuti", value: totals.requests || 0, color: "#c95a1a" },
    { label: "Email inviate", value: totals.email_sent || 0, color: "#e07a3e" },
    { label: "Email aperte", value: totals.email_opened || 0, color: "#f59e0b" },
    { label: "Click calendario", value: totals.calendar_clicks || 0, color: "#10b981" },
    { label: "Clienti", value: totals.customers || 0, color: "#15803d" },
  ];
  const max = Math.max(1, ...stages.map(s => s.value));
  return (
    <div data-testid="funnel-chart">
      {stages.map((s, i) => {
        const pct = (s.value / max) * 100;
        const conv = i > 0 && stages[i - 1].value > 0 ? (s.value / stages[i - 1].value) * 100 : null;
        return (
          <div key={s.label} style={{marginBottom: 8}}>
            <div className="flex justify-between items-center" style={{marginBottom: 4}}>
              <span className="text-small" style={{fontWeight: 600}}>{s.label}</span>
              <span className="text-small">
                <strong>{s.value}</strong>
                {conv != null && (
                  <span className="text-muted" style={{marginLeft: 8, fontSize: 11}}>
                    (→ {conv.toFixed(1)}%)
                  </span>
                )}
              </span>
            </div>
            <div style={{
              height: 22, background: "#f4f4f5", borderRadius: 4, overflow: "hidden",
            }}>
              <div style={{
                width: `${Math.max(2, pct)}%`, height: "100%", background: s.color,
                transition: "width 0.5s ease-out",
              }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function SpendModal({ onClose, onSaved, initial = null }) {
  const [form, setForm] = useState(initial || {
    source: "google_ads", channel: "", periodStart: "", periodEnd: "",
    amountEur: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.source || !form.periodStart || !form.periodEnd || !form.amountEur) {
      alert("Compila tutti i campi obbligatori");
      return;
    }
    setSaving(true);
    try {
      await api.post("/ad-spend", form);
      onSaved();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="spend-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth: 500, padding: 0}}>
        <div style={{padding:"16px 18px",borderBottom:"1px solid #eee",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontWeight: 600, fontSize: 16}}>Aggiungi spesa pubblicitaria</div>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={16}/></button>
        </div>
        <div style={{padding: 18}}>
          <label className="label">Sorgente *</label>
          <select className="input" value={form.source} onChange={e=>setForm({...form, source:e.target.value})}
            data-testid="spend-source">
            <option value="google_ads">Google Ads</option>
            <option value="facebook_ads">Facebook Ads</option>
            <option value="linkedin_ads">LinkedIn Ads</option>
            <option value="maxvalle.it">maxvalle.it (organico)</option>
            <option value="sitidaincubo.it">sitidaincubo.it (organico)</option>
            <option value="direct">Direct (altro)</option>
          </select>
          <label className="label mt-3">Canale (opzionale)</label>
          <input className="input" placeholder="es. Search · Brand"
            value={form.channel} onChange={e=>setForm({...form, channel:e.target.value})}
            data-testid="spend-channel"/>
          <div className="grid gap-2 mt-3" style={{gridTemplateColumns:"1fr 1fr"}}>
            <div>
              <label className="label">Da *</label>
              <input className="input" type="date"
                value={form.periodStart} onChange={e=>setForm({...form, periodStart:e.target.value})}
                data-testid="spend-start"/>
            </div>
            <div>
              <label className="label">A *</label>
              <input className="input" type="date"
                value={form.periodEnd} onChange={e=>setForm({...form, periodEnd:e.target.value})}
                data-testid="spend-end"/>
            </div>
          </div>
          <label className="label mt-3">Importo € *</label>
          <input className="input" type="number" step="0.01" placeholder="500.00"
            value={form.amountEur} onChange={e=>setForm({...form, amountEur:e.target.value})}
            data-testid="spend-amount"/>
          <label className="label mt-3">Note</label>
          <textarea className="input" rows={2}
            value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/>
        </div>
        <div style={{padding: "12px 18px", gap: 8, display: "flex", justifyContent: "flex-end", borderTop:"1px solid #eee"}}>
          <button onClick={onClose} className="btn btn-ghost">Annulla</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="save-spend">
            {saving ? <Loader2 size={14} className="spin"/> : <Plus size={14}/>} Salva
          </button>
        </div>
      </div>
    </div>
  );
}


function CsvImportModal({ onClose, onSaved }) {
  const [source, setSource] = useState("google_ads");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return alert("Seleziona un file CSV");
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      const { data } = await api.post("/ad-spend/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      alert("Errore import: " + (e.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="csv-modal">
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth: 540, padding: 0}}>
        <div style={{padding:"16px 18px",borderBottom:"1px solid #eee",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontWeight: 600, fontSize: 16}}>Import CSV spese pubblicitarie</div>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={16}/></button>
        </div>
        <div style={{padding: 18}}>
          <div className="text-small text-muted mb-3">
            Carica il CSV scaricato da <strong>Google Ads</strong> (Report → Esporta → CSV) o
            <strong> Facebook Ads</strong> (Ads Manager → Esporta → CSV). Le righe verranno
            aggregate per mese e attribuite alla sorgente selezionata.
            <br/><br/>
            <strong>Colonne riconosciute</strong>: data (<code>Date</code>/<code>Day</code>/<code>Giorno</code>) +
            costo (<code>Cost</code>/<code>Amount spent</code>/<code>Spesa</code>/<code>Costo</code>).
          </div>
          <label className="label">Attribuisci a sorgente *</label>
          <select className="input" value={source} onChange={e=>setSource(e.target.value)}
            data-testid="csv-source">
            <option value="google_ads">Google Ads</option>
            <option value="facebook_ads">Facebook Ads</option>
            <option value="linkedin_ads">LinkedIn Ads</option>
          </select>
          <label className="label mt-3">File CSV *</label>
          <input type="file" accept=".csv,text/csv"
            onChange={e=>setFile(e.target.files?.[0])}
            data-testid="csv-file"/>
          {result && (
            <div className="card mt-3" style={{padding: 12, background: "#dcfce7", borderLeft: "4px solid var(--mv-success)"}}>
              <div className="text-small">
                ✅ Import completato: <strong>{result.inserted}</strong> record creati ({Object.keys(result.by_month || {}).length} mesi),
                {result.skipped_rows > 0 && <> {result.skipped_rows} righe ignorate.</>}
              </div>
            </div>
          )}
        </div>
        <div style={{padding: "12px 18px", gap: 8, display: "flex", justifyContent: "flex-end", borderTop:"1px solid #eee"}}>
          <button onClick={onClose} className="btn btn-ghost">Chiudi</button>
          {result ? (
            <button onClick={onSaved} className="btn btn-primary">OK, aggiorna</button>
          ) : (
            <button onClick={upload} disabled={!file || uploading} className="btn btn-primary"
              data-testid="upload-csv">
              {uploading ? <Loader2 size={14} className="spin"/> : <Upload size={14}/>} Importa
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
